// components/groups/GroupAdminPanel.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Briefcase, 
  Settings, 
  Bell, 
  TrendingUp,
  Shield,
  AlertCircle,
  ChevronRight,
  ExternalLink
} from 'lucide-react';
import JobPostingForm from './JobPostingForm';
import Modal from '../common/Modal';
import { useTrust } from '../../context/TrustContext';
import { useAuth } from '../../context/AuthContext';
import { useAdmin } from '../../context/AdminContext';

const GroupAdminPanel = ({ group, onUpdate }) => {
  const { user } = useAuth();
  const { userTrust } = useTrust();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [showJobPostingModal, setShowJobPostingModal] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  
  // Mock data for demo - would come from backend
  const { groupJobs: allGroupJobs, postJob } = useAdmin();
  const groupJobs = allGroupJobs[group.id] || [];
  const [members] = useState([
    { id: 1, name: 'John Doe', trust: 4.2, joined: '2024-02-01', units: 3 },
    { id: 2, name: 'Sarah Lee', trust: 3.8, joined: '2024-02-15', units: 1 },
    { id: 3, name: 'Mike Chen', trust: 3.5, joined: '2024-02-20', units: 2 },
  ]);

  // Check if user is group creator
  const isCreator = group.creatorId === user?.id || group.creatorName === user?.name;
  
  // Admin metrics
  const metrics = {
    totalMembers: group.currentCount || 0,
    totalUnits: group.currentUnits || 0,
    jobsPosted: groupJobs.length,
    jobsCompleted: groupJobs.filter(j => j.status === 'completed').length,
    estimatedSavings: (group.currentUnits || 0) * (group.savings || 0) * group.currentPrice / 100,
    trustImpact: '+0.2' // Would be calculated based on group performance
  };

  const handlePostJob = async (jobData) => {
    try {
      await postJob(group.id, jobData);
      setShowJobPostingModal(false);
    } catch (error) {
      console.error('Failed to post job:', error);
      // Could add error notification here
    }
  };

  const handleAnnouncement = () => {
    // In production, this would send to all members
    console.log('Sending announcement:', announcement);
    setShowAnnouncementModal(false);
    setAnnouncement('');
  };

  if (!isCreator) {
    return null; // Don't show admin panel to non-creators
  }

  return (
    <div className="bg-white rounded-lg shadow mb-4">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-lg flex items-center">
            <Shield className="mr-2 text-blue-600" size={20} />
            Group Administrator Panel
          </h3>
          <div className="flex items-center gap-2">
            <div className={`px-3 py-1 rounded-full text-sm ${
              userTrust.score >= 4.0 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              Admin Trust: {userTrust.score}
            </div>
            {/* NEW: Link to full dashboard */}
            <button
              onClick={() => navigate(`/groups/${group.id}/admin`)}
              className="flex items-center text-blue-600 hover:bg-blue-50 px-3 py-1 rounded-lg text-sm font-medium"
            >
              Full Dashboard
              <ExternalLink size={14} className="ml-1" />
            </button>
          </div>
        </div>
        
        {/* Admin Tabs */}
        <div className="flex space-x-4 border-b -mb-4">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 px-1 border-b-2 transition-colors ${
              activeTab === 'overview' 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('jobs')}
            className={`pb-3 px-1 border-b-2 transition-colors ${
              activeTab === 'jobs' 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Jobs & Logistics
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`pb-3 px-1 border-b-2 transition-colors ${
              activeTab === 'members' 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Members
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`pb-3 px-1 border-b-2 transition-colors ${
              activeTab === 'settings' 
                ? 'border-blue-600 text-blue-600' 
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            Settings
          </button>
        </div>
      </div>

      <div className="p-4">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-3 rounded">
                <div className="text-2xl font-bold text-blue-600">{metrics.totalMembers}</div>
                <div className="text-sm text-gray-600">Members</div>
              </div>
              <div className="bg-green-50 p-3 rounded">
                <div className="text-2xl font-bold text-green-600">{metrics.totalUnits}</div>
                <div className="text-sm text-gray-600">Units Ordered</div>
              </div>
              <div className="bg-purple-50 p-3 rounded">
                <div className="text-2xl font-bold text-purple-600">{metrics.jobsPosted}</div>
                <div className="text-sm text-gray-600">Jobs Posted</div>
              </div>
              <div className="bg-yellow-50 p-3 rounded">
                <div className="text-2xl font-bold text-yellow-600">{metrics.trustImpact}</div>
                <div className="text-sm text-gray-600">Trust Impact</div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-4 mb-4">
              <h4 className="font-semibold text-blue-900 mb-2">💡 Admin Tip</h4>
              <p className="text-sm text-blue-800">
                Post logistics jobs early to ensure smooth pickup coordination. 
                Members with verified skills will see your jobs on the Logistics page!
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => setShowJobPostingModal(true)}
                className="w-full bg-blue-600 text-white rounded-lg py-3 font-semibold hover:bg-blue-700 flex items-center justify-center"
              >
                <Briefcase size={18} className="mr-2" />
                Post Logistics Job
              </button>
              
              <button
                onClick={() => setShowAnnouncementModal(true)}
                className="w-full border border-blue-600 text-blue-600 rounded-lg py-3 font-semibold hover:bg-blue-50 flex items-center justify-center"
              >
                <Bell size={18} className="mr-2" />
                Send Announcement
              </button>

              {/* NEW: Link to full dashboard */}
              <button
                onClick={() => navigate(`/groups/${group.id}/admin`)}
                className="w-full bg-gray-100 text-gray-700 rounded-lg py-3 font-semibold hover:bg-gray-200 flex items-center justify-center"
              >
                <TrendingUp size={18} className="mr-2" />
                View Full Dashboard
              </button>
            </div>
          </div>
        )}

        {/* Jobs Tab */}
        {activeTab === 'jobs' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-semibold">Posted Jobs ({groupJobs.length})</h4>
              <button
                onClick={() => setShowJobPostingModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700"
              >
                + New Job
              </button>
            </div>

            {groupJobs.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded">
                <Briefcase size={48} className="mx-auto text-gray-400 mb-3" />
                <p className="text-gray-600 mb-3">No jobs posted yet</p>
                <p className="text-sm text-gray-500 mb-4">
                  Post logistics jobs to utilize verified skills in your community
                </p>
                <button
                  onClick={() => setShowJobPostingModal(true)}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700"
                >
                  Post First Job
                </button>
              </div>
            ) : (
              <div className="space-y-3">
                {groupJobs.map(job => (
                  <div key={job.id} className="border rounded-lg p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h5 className="font-semibold">{job.title}</h5>
                        <p className="text-sm text-gray-600 mt-1">
                          Required: {job.requiredSkills?.map(s => s.name).join(', ') || 'None'}
                        </p>
                        <div className="flex items-center mt-2 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            job.status === 'open' ? 'bg-green-100 text-green-700' :
                            job.status === 'claimed' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {job.status.toUpperCase()}
                          </span>
                          <span className="ml-3 text-gray-500">
                            {job.applicants || 0} applicants
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-green-600">
                          {job.tokenReward} tokens
                        </div>
                        <button 
                          onClick={() => navigate(`/groups/${group.id}/admin`)}
                          className="text-blue-600 text-sm hover:underline mt-1"
                        >
                          View Details
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Link to dashboard for full job management */}
                <div className="text-center pt-2">
                  <button
                    onClick={() => navigate(`/groups/${group.id}/admin`)}
                    className="text-blue-600 text-sm hover:underline"
                  >
                    Manage all jobs in dashboard →
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div>
            <div className="mb-4">
              <h4 className="font-semibold mb-2">Group Members ({members.length})</h4>
              <div className="text-sm text-gray-600">
                Total units ordered: {metrics.totalUnits}
              </div>
            </div>

            <div className="space-y-2">
              {members.map(member => (
                <div key={member.id} className="border rounded-lg p-3 flex justify-between items-center">
                  <div>
                    <div className="font-semibold">{member.name}</div>
                    <div className="text-sm text-gray-600">
                      Trust: {member.trust} • Units: {member.units}
                    </div>
                  </div>
                  <button className="text-blue-600 hover:bg-blue-50 p-2 rounded">
                    <ChevronRight size={18} />
                  </button>
                </div>
              ))}
            </div>

            {/* Link to dashboard for full member management */}
            <div className="text-center mt-4">
              <button
                onClick={() => navigate(`/groups/${group.id}/admin`)}
                className="text-blue-600 text-sm hover:underline"
              >
                View all members and details →
              </button>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Pickup Details</h4>
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-sm text-gray-700">
                    <strong>Location:</strong> {group.pickupLocation}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Address:</strong> {group.pickupAddress}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Time:</strong> {group.pickupTime}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-2">Logistics Tier</h4>
                <div className="border rounded-lg p-3">
                  <select className="w-full p-2 border rounded">
                    <option value="1">Self-Managed (You coordinate)</option>
                    <option value="2">Platform-Managed (We help coordinate)</option>
                    <option value="3">Full-Service (We handle everything)</option>
                  </select>
                </div>
              </div>

              <button 
                onClick={() => navigate(`/groups/${group.id}/admin`)}
                className="w-full bg-gray-200 text-gray-700 rounded-lg py-3 font-semibold hover:bg-gray-300"
              >
                <Settings size={18} className="inline mr-2" />
                Advanced Settings
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Job Posting Modal */}
      <Modal
        isOpen={showJobPostingModal}
        onClose={() => setShowJobPostingModal(false)}
        title="Post Logistics Job"
        size="large"
      >
        <JobPostingForm 
          groupId={group.id}
          groupTitle={group.title}
          onPost={handlePostJob}
          onCancel={() => setShowJobPostingModal(false)}
        />
      </Modal>

      {/* Announcement Modal */}
      <Modal
        isOpen={showAnnouncementModal}
        onClose={() => setShowAnnouncementModal(false)}
        title="Send Group Announcement"
        size="medium"
      >
        <div>
          <textarea
            value={announcement}
            onChange={(e) => setAnnouncement(e.target.value)}
            placeholder="Type your message to all group members..."
            className="w-full p-3 border rounded-lg h-32 resize-none"
          />
          <div className="flex justify-end space-x-3 mt-4">
            <button
              onClick={() => setShowAnnouncementModal(false)}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleAnnouncement}
              disabled={!announcement.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              Send to All Members
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default GroupAdminPanel;